"use client"

import React, { useEffect, useRef, useState } from "react"
import { X, Mail, Paperclip, Loader2, AlertTriangle, Copy, Check, ShieldAlert, Clock } from "lucide-react"
import DOMPurify from "isomorphic-dompurify"
import { Sheet, SheetPortal } from "@/components/ui/sheet"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useResizableDrawer, DragHandle } from "@/components/shared/resizable-drawer"
import { copyTextToClipboard } from "@/utils/clipboard"
import type { VmsEmailPreview } from "@/types/vms/vms-requests"

interface VmsEmailPreviewPanelProps {
  isOpen: boolean
  onClose: () => void
  emailId: string
  /** Used purely for the panel header. Falls back to "Email" when omitted. */
  title?: string
}

// Width tuning matches Related Requests' drawer so the two panels feel like
// siblings. Wider on the full side than RRP though — emails benefit from
// more horizontal room than request rows do.
const COMPACT_WIDTH = 640
const FULL_WIDTH = 1100
const SNAP_THRESHOLD_RATIO = 0.42
const STORAGE_WIDTH_KEY = "vms-email-preview-drawer-width"

/**
 * Read-only slide-out for the decrypted VMS email body + attachment metadata.
 *
 * Uses the same resizable Sheet drawer pattern as RelatedRequestsPanel —
 * shared via @/components/shared/resizable-drawer so the drag handle, snap
 * behaviour, and animation all match. Body is decrypted server-side; the
 * encrypted ciphertext never reaches the wire.
 *
 * HTML bodies are sanitized with DOMPurify (scripts/iframes/forms/styles
 * stripped; anchors get rel="noopener noreferrer nofollow" target="_blank"
 * via a hook). Plain-text bodies render in a monospace block with whitespace
 * preserved so the sender's line breaks survive.
 */
export function VmsEmailPreviewPanel({ isOpen, onClose, emailId, title }: VmsEmailPreviewPanelProps) {
  const { fetchVmsEmailPreview } = useWorkflowApi()
  const [preview, setPreview] = useState<VmsEmailPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Track which body view the user picked; default to HTML when available,
  // plain text otherwise. We swap on the fly via the segmented control.
  const [bodyView, setBodyView] = useState<'html' | 'text'>('html')
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Dedupe in-flight requests across rapid re-opens. React strict mode
  // triggers the effect twice on mount in dev; without this we'd hit the
  // API twice and the second response could clobber the first.
  const inflightRef = useRef<string | null>(null)

  // Same resizable drawer as RelatedRequestsPanel — shared hook, separate
  // storage key + widths so each panel remembers its own preference.
  const { currentWidth, dragging, onHandleMouseDown, toggleMode, isFull } = useResizableDrawer({
    compactWidth: COMPACT_WIDTH,
    fullWidth: FULL_WIDTH,
    snapThresholdRatio: SNAP_THRESHOLD_RATIO,
    storageKey: STORAGE_WIDTH_KEY,
  })

  // Refetch whenever the email id changes while open. Closing clears the
  // previous body AND resets the in-flight tag so re-opening the same email
  // (very common after the user edits a field on the underlying request and
  // wants to confirm the change) refetches instead of replaying the cached
  // old response.
  useEffect(() => {
    if (!isOpen) {
      setPreview(null)
      setError(null)
      inflightRef.current = null
      return
    }
    if (!emailId) return
    if (inflightRef.current === emailId) return
    inflightRef.current = emailId
    setLoading(true)
    setError(null)
    fetchVmsEmailPreview(emailId)
      .then(p => {
        if (inflightRef.current !== emailId) return
        if (!p) {
          setError("This email could not be loaded. It may have been deleted or you may not have access.")
          return
        }
        setPreview(p)
        // Pick the best initial body view based on what's actually present.
        setBodyView(p.bodyHtml && p.bodyHtml.trim().length > 0 ? 'html' : 'text')
      })
      .catch(() => {
        if (inflightRef.current !== emailId) return
        setError("Failed to load email preview")
      })
      .finally(() => {
        if (inflightRef.current === emailId) setLoading(false)
      })
    // fetchVmsEmailPreview is stable from useApi; depending on it would
    // refetch on every render. Intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, isOpen])

  // Sanitize HTML body — DOMPurify defaults plus our own anchor hardening.
  // <a> tags get rel="noopener noreferrer nofollow" target="_blank" so a
  // hostile email can't navigate the parent window or leak the referrer.
  useEffect(() => {
    const hookName = 'afterSanitizeAttributes'
    const hook = (node: Element) => {
      if (node.tagName === 'A') {
        node.setAttribute('rel', 'noopener noreferrer nofollow')
        node.setAttribute('target', '_blank')
      }
    }
    DOMPurify.addHook(hookName, hook)
    return () => { DOMPurify.removeHook(hookName) }
  }, [])

  const handleCopy = async (value: string, field: string) => {
    // Route through the shared helper so the textarea/execCommand fallback
    // kicks in when navigator.clipboard is unavailable (Chrome non-secure
    // contexts). Without the fallback this path would silently soft-fail
    // in HTTP-served instances — the green checkmark wouldn't flash but
    // there's no other signal that the copy didn't work, so the user could
    // think they got an empty clipboard rather than realize the API is the
    // limiter. Helper returns true only when something was actually copied,
    // which gates the success UI.
    const ok = await copyTextToClipboard(value)
    if (!ok) return
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 1500)
  }

  // We deliberately KEEP <img> in the sanitized output — support staff need
  // to see brand banners, layout cues, and (yes) tracking pixels. <style> and
  // <link> are dropped to kill CSS-injection vectors.
  const sanitizedHtml = preview?.bodyHtml
    ? DOMPurify.sanitize(preview.bodyHtml, {
        FORBID_TAGS: ['script', 'iframe', 'form', 'object', 'embed', 'style', 'link'],
        FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'srcdoc'],
      })
    : ''

  const formatTimestamp = (iso?: string | null) => {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso
    }
  }

  const hasHtml = !!(preview?.bodyHtml && preview.bodyHtml.trim().length > 0)
  const hasPlain = !!(preview?.bodyPlain && preview.bodyPlain.trim().length > 0)

  return (
    <Sheet open={isOpen} onOpenChange={open => { if (!open) onClose() }}>
      <SheetPortal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <SheetPrimitive.Content
          style={{
            width: currentWidth,
            maxWidth: '100vw',
            transition: dragging ? "none" : "width 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
            position: "fixed",
            insetBlock: 0,
            right: 0,
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#FFFFFF",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.10)",
            overflow: "visible",
          }}
        >
          <DragHandle
            onMouseDown={onHandleMouseDown}
            onToggle={toggleMode}
            isFull={isFull}
            expandLabel="Expand email preview"
            collapseLabel="Collapse email preview"
          />

          {/* Header — subject up top, ID row underneath. Maximize moved to
              the drag handle on the left edge to match Related Requests. */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Mail className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <h2 className="text-base font-semibold text-gray-900 break-words leading-snug">
                  {preview?.subject || title || 'Email'}
                </h2>
              </div>
              <SheetPrimitive.Close
                className="p-1 rounded hover:bg-gray-100 flex-shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-500" />
              </SheetPrimitive.Close>
            </div>
            {/* Id priority: extracted external id → linked VmsRequest cosmos
                id → email's own cosmos id (last resort for orphan emails). */}
            {(preview?.vmsExternalRequestId || preview?.linkedVmsRequestId || preview?.id) && (
              <p className="mt-1.5 ml-7 text-sm text-gray-600 break-all">
                <span className="text-gray-400 mr-1.5">ID:</span>
                <span className="font-mono text-gray-700">
                  {preview.vmsExternalRequestId || preview.linkedVmsRequestId || preview.id}
                </span>
              </p>
            )}
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="px-5 py-4 space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-12 text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading email…
                </div>
              )}

              {!loading && error && (
                <div className="flex items-start gap-2 p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {!loading && !error && preview && (
                <>
                  {/* Metadata strip */}
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                    <dt className="text-gray-500">From</dt>
                    <dd className="flex items-center gap-2 min-w-0 break-all">
                      <span className="text-gray-900">{preview.from || '—'}</span>
                      {preview.from && (
                        <button
                          className="text-gray-400 hover:text-gray-700"
                          onClick={() => handleCopy(preview.from!, 'from')}
                          aria-label="Copy sender"
                        >
                          {copiedField === 'from'
                            ? <Check className="h-3.5 w-3.5 text-green-600" />
                            : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </dd>

                    <dt className="text-gray-500">To</dt>
                    <dd className="text-gray-900 break-all">{preview.to || '—'}</dd>

                    {preview.originalSentAt && (
                      <>
                        <dt className="text-gray-500">Sent</dt>
                        <dd className="flex items-center gap-1.5 text-gray-900">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          {formatTimestamp(preview.originalSentAt)}
                        </dd>
                      </>
                    )}

                    {preview.mailgunReceivedAt && (
                      <>
                        <dt className="text-gray-500">Received</dt>
                        <dd className="text-gray-900">{formatTimestamp(preview.mailgunReceivedAt)}</dd>
                      </>
                    )}

                    {preview.status && (
                      <>
                        <dt className="text-gray-500">Status</dt>
                        <dd className="text-gray-900">{preview.status}</dd>
                      </>
                    )}
                  </dl>

                  {/* Notices */}
                  {preview.isSpam && (
                    <div className="flex items-center gap-2 p-2.5 rounded border border-rose-200 bg-rose-50 text-rose-900 text-xs">
                      <ShieldAlert className="h-4 w-4 flex-shrink-0" />
                      <span>This email was classified as spam.</span>
                    </div>
                  )}
                  {preview.errorReason && (
                    <div className="flex items-center gap-2 p-2.5 rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>{preview.errorReason}</span>
                    </div>
                  )}
                  {preview.isConfigFallback && (
                    <div className="flex items-center gap-2 p-2.5 rounded border border-blue-200 bg-blue-50 text-blue-900 text-xs">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>Processed with a fallback configuration (the source endpoint setting was inactive at receive time).</span>
                    </div>
                  )}

                  {/* Body — moved above attachments. Email clients (Gmail,
                      Outlook) all show body first because that's what the user
                      is actually here to read; attachments are a footer-style
                      footnote underneath. */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-700">
                        Body
                      </div>
                      {hasHtml && hasPlain && (
                        <div className="inline-flex rounded border border-gray-200 overflow-hidden text-xs">
                          <button
                            className={`px-2 py-0.5 ${bodyView === 'html' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => setBodyView('html')}
                          >
                            Rendered
                          </button>
                          <button
                            className={`px-2 py-0.5 ${bodyView === 'text' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                            onClick={() => setBodyView('text')}
                          >
                            Plain text
                          </button>
                        </div>
                      )}
                    </div>

                    {bodyView === 'html' && hasHtml && (
                      <div
                        className="rounded border border-gray-200 bg-white p-3 text-sm text-gray-900 max-w-none [&_a]:text-blue-600 [&_a]:underline [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                      />
                    )}

                    {(bodyView === 'text' || !hasHtml) && hasPlain && (
                      <pre className="rounded border border-gray-200 bg-gray-50 p-3 text-xs font-mono text-gray-900 whitespace-pre-wrap break-words overflow-x-auto">
                        {preview.bodyPlain}
                      </pre>
                    )}

                    {!hasHtml && !hasPlain && (
                      <div className="text-sm text-gray-400 italic px-3 py-4 text-center">
                        (Body is empty.)
                      </div>
                    )}
                  </div>

                  {/* Attachments — under the body. Always rendered (even when
                      empty) so the user can confirm at a glance whether
                      anything is attached, without having to wonder if the
                      section is missing because of empty state or a bug. */}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1.5">
                      Attachments ({preview.attachments?.length ?? 0})
                    </div>
                    {preview.attachments && preview.attachments.length > 0 ? (
                      <ul className="space-y-1">
                        {preview.attachments.map((att, i) => (
                          <li
                            key={`${att.fileName}-${i}`}
                            className="flex items-center gap-2 px-2.5 py-1.5 rounded border border-gray-200 bg-gray-50 text-sm"
                          >
                            <Paperclip className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-900 truncate flex-1">
                              {att.fileName || 'Attachment'}
                            </span>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              {formatBytes(att.fileSize)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-2.5 py-1.5 rounded border border-dashed border-gray-200 text-xs text-gray-400 italic">
                        No attachments
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  )
}

/** Bytes → human-friendly. Mirrors what most file-upload chips render. */
function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}
